import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Link as MuiLink,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import GlobalHeader from "../../../components/GlobalHeader";
import SnackAlert from "../../../components/SnackAlert";
import useSnack from "../../../hooks/useSnack";
import useApi from "../../../hooks/useApi";
import countries from "../../../utils/countries";
import FormArtista from "./FormArtista";
import CancionesDelArtista from "./CancionesDelArtista";
import ListaEventos from "../../../components/ListaEventos";
import { hrefSeguro } from "../../../utils/hrefSeguro";

const REDES = [
  { clave: "instagram", label: "Instagram" },
  { clave: "tiktok", label: "TikTok" },
  { clave: "spotify", label: "Spotify" },
  { clave: "youtube", label: "YouTube" },
  { clave: "facebook", label: "Facebook" },
  { clave: "twitter", label: "X (Twitter)" },
  { clave: "pagina_web", label: "Página web" },
];

// Ficha de un artista del roster. Es el lugar desde donde el sello le carga
// todo su material: sus datos, su presskit, sus canciones y sus eventos.
export default function FichaArtista() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { loadApi, loadingApi } = useApi();
  const { msgSB, openSB, showSnack, typeSB } = useSnack();
  const [artista, setArtista] = useState(null);
  const [noExiste, setNoExiste] = useState(false);
  const [formAbierto, setFormAbierto] = useState(false);

  const cargar = useCallback(() => {
    loadApi(`artista/roster/${id}`, true, "get")
      .then((res) => setArtista(res.data))
      .catch((e) => {
        setNoExiste(true);
        showSnack(e.message, "error");
      });
  }, [id]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const guardar = (form, imagen, presskit, quitarPresskit) => {
    const body = {
      nombre: form.nombre,
      descripcion: form.descripcion,
      pais: form.pais,
      generos_musicales: JSON.stringify(form.generos_musicales || []),
      redes: JSON.stringify(form.redes || {}),
    };
    if (imagen) body.imagen = imagen;
    if (presskit) body.presskit = presskit;
    // Solo se manda la marca cuando hay que borrarlo: no mandar el archivo
    // significa "no lo toques", igual que con las redes.
    if (quitarPresskit) body.quitar_presskit = "true";

    loadApi(`artista/roster/${id}`, true, "put", body, true)
      .then((res) => {
        showSnack(res.data.message);
        setFormAbierto(false);
        cargar();
      })
      .catch((e) => showSnack(e.message, "error"));
  };

  if (noExiste) {
    return (
      <>
        <GlobalHeader />
        <Container>
          <Box textAlign="center" py={8}>
            <Typography variant="h6" color="text.secondary" mb={2}>
              Este artista no existe en tu roster.
            </Typography>
            <Button variant="contained" onClick={() => navigate("/artista/roster")}>
              Volver a mis artistas
            </Button>
          </Box>
        </Container>
        <SnackAlert openSB={openSB} typeSB={typeSB} msgSB={msgSB} />
      </>
    );
  }

  if (!artista) {
    return (
      <>
        <GlobalHeader />
        <Container>
          <Typography color="text.secondary" py={6}>
            Cargando…
          </Typography>
        </Container>
      </>
    );
  }

  const redesCargadas = REDES.filter(({ clave }) => artista.redes?.[clave]);

  return (
    <>
      <GlobalHeader />
      <Container>
        <Button component={Link} to="/artista/roster" sx={{ mb: 1 }}>
          &lt; Mis artistas
        </Button>

        <Card>
          <CardContent>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={3}
              alignItems={{ xs: "flex-start", sm: "center" }}
            >
              <Avatar
                src={artista.imagen || undefined}
                variant="rounded"
                sx={{ width: 120, height: 120 }}
              >
                {artista.nombre?.[0]?.toUpperCase()}
              </Avatar>

              <Stack flex={1} spacing={1}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  flexWrap="wrap"
                >
                  <Typography variant="h4" fontWeight={900} color="primary.main">
                    {artista.nombre}
                  </Typography>
                  {!artista.activo && <Chip label="Dado de baja" size="small" />}
                </Stack>

                {artista.pais && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <img
                      src={`https://flagcdn.com/${artista.pais}.svg`}
                      alt=""
                      width="24"
                      height="16"
                    />
                    <Typography variant="body2" color="text.secondary">
                      {countries[artista.pais] || artista.pais}
                    </Typography>
                  </Stack>
                )}

                <Stack direction="row" gap={0.5} flexWrap="wrap">
                  {(artista.generos_musicales || []).map((genero) => (
                    <Chip key={genero} label={genero} size="small" />
                  ))}
                </Stack>
              </Stack>

              <Stack spacing={1}>
                <Button variant="outlined" onClick={() => setFormAbierto(true)}>
                  Editar ficha
                </Button>
                {/* El historial es uno solo para todo el ecosistema; entrar
                    desde acá lo abre ya filtrado por este artista. */}
                <Button
                  component={Link}
                  to={`/artista/envios?artista=${artista.id}`}
                  size="small"
                >
                  Ver sus envíos
                </Button>
              </Stack>
            </Stack>

            {artista.descripcion && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" whiteSpace="pre-line">
                  {artista.descripcion}
                </Typography>
              </>
            )}

            {redesCargadas.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Stack direction="row" gap={1} flexWrap="wrap">
                  {redesCargadas.map(({ clave, label }) => (
                    <MuiLink
                      key={clave}
                      href={hrefSeguro(artista.redes[clave])}
                      target="_blank"
                      rel="noreferrer"
                      underline="hover"
                    >
                      <Chip label={label} size="small" clickable />
                    </MuiLink>
                  ))}
                </Stack>
              </>
            )}
          </CardContent>
        </Card>

        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={700} mb={1}>
              Presskit
            </Typography>
            {artista.presskit ? (
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                flexWrap="wrap"
              >
                <Button
                  variant="outlined"
                  href={artista.presskit}
                  target="_blank"
                  rel="noreferrer"
                >
                  Descargar presskit
                </Button>
                <Typography variant="body2" color="text.secondary">
                  Es lo que va a recibir el difusor junto a los envíos de{" "}
                  {artista.nombre}.
                </Typography>
              </Stack>
            ) : (
              <Stack spacing={1} alignItems="flex-start">
                <Typography variant="body2" color="text.secondary">
                  Todavía no cargaste el presskit de {artista.nombre}. Es el
                  material que va a recibir el difusor junto a sus envíos.
                </Typography>
                <Button variant="outlined" onClick={() => setFormAbierto(true)}>
                  Subir presskit
                </Button>
              </Stack>
            )}
          </CardContent>
        </Card>

        <CancionesDelArtista artista={artista} showSnack={showSnack} />

        <Card sx={{ mt: 2 }}>
          <CardContent>
            <ListaEventos
              artista={artista}
              showSnack={showSnack}
              titulo={`Eventos de ${artista.nombre}`}
            />
          </CardContent>
        </Card>
      </Container>

      <FormArtista
        abierto={formAbierto}
        artista={artista}
        onCerrar={() => setFormAbierto(false)}
        onGuardar={guardar}
        guardando={loadingApi.includes(`put__artista/roster/${id}`)}
        showSnack={showSnack}
      />

      <SnackAlert openSB={openSB} typeSB={typeSB} msgSB={msgSB} />
    </>
  );
}
